// frontend/src/components/surveys/NPSFollowUp.js

/**
 * NPS Follow-Up Component
 * Displays different actions based on NPS score:
 * - Promoters (4-5 stars): Suggest inviting others
 * - Detractors (1-3 stars): Request improvement feedback
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, MessageSquare, CheckCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import ThemedButton from '../themed/ThemedButton';
import ThemedCard from '../themed/ThemedCard';

const NPSFollowUp = ({ isPromoter, message, onInvite, onDismiss }) => {
    const { t } = useTranslation();
    const { text } = useTheme();

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <ThemedCard className="max-w-lg w-full p-8">
                <div className="text-center">
                    {/* Icon */}
                    <div className="mb-6">
                        {isPromoter ? (
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full mb-4">
                                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
                            </div>
                        ) : (
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 dark:bg-blue-900 rounded-full mb-4">
                                <MessageSquare className="w-12 h-12 text-blue-600 dark:text-blue-400" />
                            </div>
                        )}
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl font-bold mb-4">
                        {isPromoter
                            ? t('survey.npsFollowUp.promoterTitle')
                            : t('survey.npsFollowUp.detractorTitle')}
                    </h2>

                    {/* Message */}
                    <p className={`${text.secondary} text-lg mb-8`}>
                        {message}
                    </p>

                    {/* Actions */}
                    <div className="space-y-3">
                        {isPromoter ? (
                            <>
                                <ThemedButton
                                    onClick={onInvite}
                                    className="w-full"
                                    size="lg"
                                >
                                    <UserPlus className="w-5 h-5 mr-2" />
                                    {t('survey.npsFollowUp.inviteButton')}
                                </ThemedButton>

                                <ThemedButton
                                    onClick={onDismiss}
                                    variant="ghost"
                                    className="w-full"
                                >
                                    {t('survey.npsFollowUp.maybeLaterButton')}
                                </ThemedButton>
                            </>
                        ) : (
                            <ThemedButton
                                onClick={onDismiss}
                                className="w-full"
                                size="lg"
                            >
                                {t('survey.npsFollowUp.doneButton')}
                            </ThemedButton>
                        )}
                    </div>
                </div>
            </ThemedCard>
        </div>
    );
};

export default NPSFollowUp;
